import {
  packers_and_movers,
  service_category_2w,
  service_category_trucks,
} from '../constants/VehicleStoreEn';

export const CATEGORY = {
  TWO_WHEELERS: 0,
  CANTER_3_TONNE: 1,
  PICKUP_1_TONNE: 2,
  ONE_TON_7_FEET: 3,
  TRUCKS: 4,
  COMPACT: 5,
  COURIER: 6,
  PACKERS_MOVERS: 7,
  PARTLOAD: 8,
} as const;

export interface VehicleCity {
  city_name: string;
  city_services: Array<{ text: string }>;
}

interface NamedCity {
  city_name: string;
}

function createRedirectionLinkInt(
  serviceCategory: unknown,
  cityName: string,
): string {
  switch (serviceCategory) {
    case CATEGORY.TWO_WHEELERS:
      return `/bike-parcel-delivery-${cityName}`;
    case CATEGORY.PACKERS_MOVERS:
      return `/packers-and-movers/${cityName}`;
    default:
      return `/rent-mini-trucks-in-${cityName}`;
  }
}

function createRedirectionLinkDom(
  serviceCategory: unknown,
  cityName: string,
): string {
  switch (serviceCategory) {
    case CATEGORY.TWO_WHEELERS:
      return `/two-wheelers/${cityName}`;
    case CATEGORY.PACKERS_MOVERS:
      return `/packers-and-movers/${cityName}`;
    default:
      return `/trucks/${cityName}`;
  }
}

function getRedirectionPath(
  locale: string,
  serviceCategory: unknown,
  cityName: string,
): string {
  if (locale === 'en-IN') {
    return serviceCategory === CATEGORY.TWO_WHEELERS
      ? `/two-wheelers/${cityName}`
      : `/trucks/${cityName}`;
  }
  return serviceCategory === CATEGORY.TWO_WHEELERS
    ? `/bike-parcel-delivery-${cityName}`
    : `/rent-mini-trucks-in-${cityName}`;
}

export function getRedirectionLink(
  cityData: VehicleCity,
  serviceCategory: unknown,
  locale: string | undefined,
): string {
  const hasService = cityData.city_services.length > 0;
  if (hasService) {
    return locale === 'en-IN'
      ? createRedirectionLinkDom(serviceCategory, cityData.city_name)
      : createRedirectionLinkInt(serviceCategory, cityData.city_name);
  }
  return getRedirectionPath(
    locale ?? '',
    serviceCategory,
    cityData.city_name,
  ).toLocaleLowerCase();
}

const CATEGORY_STRING_MAP: Record<string, number> = {
  '2 wheeler': CATEGORY.TWO_WHEELERS,
  'two wheeler': CATEGORY.TWO_WHEELERS,
  'two_wheeler': CATEGORY.TWO_WHEELERS,
  'bike': CATEGORY.TWO_WHEELERS,
  'trucks': CATEGORY.TRUCKS,
  'truck': CATEGORY.TRUCKS,
  'compact': CATEGORY.COMPACT,
  'courier': CATEGORY.COURIER,
  'packers-movers': CATEGORY.PACKERS_MOVERS,
};

export function getCategoryFromString(vehicleType: string): number | undefined {
  return vehicleType in CATEGORY_STRING_MAP
    ? CATEGORY_STRING_MAP[vehicleType]
    : undefined;
}

export function serviceCategoryToString(serviceCategory: unknown): string {
  switch (serviceCategory) {
    case CATEGORY.TWO_WHEELERS:
      return '2w';
    case CATEGORY.PARTLOAD:
      return 'partload';
    default:
      return 'trucks';
  }
}

export function serviceCategoryToGAString(serviceCategory: unknown): string {
  switch (serviceCategory) {
    case CATEGORY.TWO_WHEELERS:
      return '2W';
    case CATEGORY.PARTLOAD:
      return 'Partload';
    default:
      return 'Trucks';
  }
}

function byteOrderCompare(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function sortCityServicesByName<T extends NamedCity>(cities: T[]): T[] {
  return [...cities].sort((a, b) => byteOrderCompare(a.city_name, b.city_name));
}

export function filterBasedOnService<T extends VehicleCity>(
  allCities: T[],
  serviceCategory: unknown,
): T[] {
  let serviceIdentifier: string;
  switch (serviceCategory) {
    case CATEGORY.TWO_WHEELERS:
      serviceIdentifier = service_category_2w;
      break;
    case CATEGORY.PACKERS_MOVERS:
      serviceIdentifier = packers_and_movers;
      break;
    default:
      serviceIdentifier = service_category_trucks;
      break;
  }

  const filtered = allCities.filter(city =>
    city.city_services.some(
      service =>
        service.text.toLocaleLowerCase() ===
        serviceIdentifier.toLocaleLowerCase(),
    ),
  );
  return sortCityServicesByName(filtered);
}
